import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Tracks the delivery → opened → completed funnel for employees invited via
 * bulk e-mail. Data source: GET /api/assessments/{aid}/bulk-invites enriched
 * with computed `status` by the backend.
 */
const STATUS_META = {
  failed:    { label: "Falha no envio", color: "bg-rose-100 text-rose-900 border-rose-300",       icon: "fa-circle-xmark" },
  pending:   { label: "Aguardando abertura", color: "bg-slate-100 text-slate-700 border-slate-300", icon: "fa-paper-plane" },
  opened:    { label: "Abriu o link", color: "bg-amber-100 text-amber-900 border-amber-300",      icon: "fa-envelope-open" },
  completed: { label: "Respondeu", color: "bg-emerald-100 text-emerald-900 border-emerald-300",   icon: "fa-circle-check" },
};

function fmt(dt) {
  return dt ? new Date(dt).toLocaleString("pt-BR") : "—";
}

function KpiCard({ label, value, tone = "default", testid }) {
  const toneCls = {
    default: "text-slate-900",
    emerald: "text-emerald-900",
    amber:   "text-amber-700",
    rose:    "text-rose-700",
    slate:   "text-slate-600",
  }[tone];
  return (
    <div className="card border-slate-200" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`kpi-number text-4xl mt-2 ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function InvitesTab({ assessmentId, onOpenInviteModal }) {
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, agg] = await Promise.all([
        api.get(`/assessments/${assessmentId}/bulk-invites`),
        api.get(`/assessments/${assessmentId}/invite-stats`),
      ]);
      setInvites(list.data);
      setStats(agg.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return invites;
    return invites.filter((it) => it.status === filter);
  }, [invites, filter]);

  const completionPct = useMemo(() => {
    if (!stats || stats.delivered === 0) return 0;
    return Math.round((stats.completed / stats.delivered) * 100);
  }, [stats]);

  if (loading && !stats) {
    return (
      <div className="text-slate-500 py-12 text-center">
        <i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando convites…
      </div>
    );
  }

  if (!stats || stats.total === 0) {
    return (
      <div className="card text-center py-16" data-testid="invites-empty">
        <i className="fa-solid fa-paper-plane text-4xl text-slate-300 mb-4"></i>
        <h3 className="font-display text-2xl font-bold text-slate-900">Nenhum convite enviado</h3>
        <p className="text-slate-600 mt-2 max-w-md mx-auto">
          Use o botão <strong>Convidar por e-mail</strong> no topo da avaliação para enviar o link
          anônimo aos colaboradores e acompanhar a participação aqui.
        </p>
        {onOpenInviteModal && (
          <button onClick={onOpenInviteModal} className="btn btn-primary mt-6" data-testid="invites-empty-cta">
            <i className="fa-solid fa-paper-plane"></i> Convidar colaboradores
          </button>
        )}
      </div>
    );
  }

  const FILTERS = [
    ["all",       `Todos (${stats.total})`],
    ["pending",   `Aguardando (${stats.pending})`],
    ["opened",    `Abriram (${stats.opened - stats.completed})`],
    ["completed", `Responderam (${stats.completed})`],
    ["failed",    `Falhas (${stats.failed})`],
  ];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Convidados" value={stats.total} testid="kpi-invites-total" />
        <KpiCard label="Entregues" value={stats.delivered} tone="emerald" testid="kpi-invites-delivered" />
        <KpiCard label="Abriram" value={stats.opened} tone="amber" testid="kpi-invites-opened" />
        <KpiCard label="Responderam" value={stats.completed} tone="emerald" testid="kpi-invites-completed" />
        <KpiCard label="Falhas" value={stats.failed} tone="rose" testid="kpi-invites-failed" />
      </div>

      {/* Conversion bar */}
      <div className="card border-emerald-200 bg-emerald-50">
        <div className="flex justify-between items-baseline mb-3">
          <div className="overline text-emerald-900">Taxa de resposta (entregues → completos)</div>
          <div className="font-display text-3xl font-extrabold text-emerald-900" data-testid="invites-completion-pct">
            {completionPct}%
          </div>
        </div>
        <div className="h-2 bg-white border border-emerald-200">
          <div className="h-full bg-emerald-900 transition-all" style={{ width: `${completionPct}%` }}></div>
        </div>
        <p className="text-xs text-emerald-900/80 mt-3">
          {stats.completed} de {stats.delivered} convites entregues resultaram em respostas concluídas.
        </p>
      </div>

      {/* Filters + reload */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`invites-filter-${k}`}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${
              filter === k
                ? "bg-emerald-900 text-white border-emerald-900"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
            }`}
          >
            {label}
          </button>
        ))}
        <button onClick={load} className="btn btn-secondary text-xs ml-auto" data-testid="invites-reload">
          <i className="fa-solid fa-arrows-rotate"></i> Atualizar
        </button>
      </div>

      {/* Table */}
      <div className="card">
        {filtered.length === 0 ? (
          <div className="text-slate-500 py-12 text-center text-sm">
            <i className="fa-solid fa-filter-circle-xmark text-2xl mb-2 text-slate-300 block"></i>
            Nenhum convite neste filtro.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 font-semibold uppercase text-xs">E-mail</th>
                <th className="font-semibold uppercase text-xs">Status</th>
                <th className="font-semibold uppercase text-xs">Enviado em</th>
                <th className="font-semibold uppercase text-xs">Abriu em</th>
                <th className="font-semibold uppercase text-xs">Respondeu em</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const meta = STATUS_META[it.status] || STATUS_META.pending;
                return (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`invite-row-${it.id}`}>
                    <td className="py-3 font-mono text-xs text-slate-800">{it.email}</td>
                    <td>
                      <span className={`${meta.color} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}
                            data-testid={`invite-status-${it.id}`}>
                        <i className={`fa-solid ${meta.icon} mr-1`}></i>{meta.label}
                      </span>
                    </td>
                    <td className="text-slate-500 font-mono text-xs">{fmt(it.sent_at)}</td>
                    <td className="text-slate-500 font-mono text-xs">{fmt(it.opened_at)}</td>
                    <td className="text-slate-500 font-mono text-xs">{fmt(it.responded_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
